import { useLayoutEffect, useRef, useState } from 'react';
import ValueWithLabelCard from 'src/components/primitives/ValueWithLabelCard/ValueWithLabelCard';
import { WithClassName } from 'src/utils/UtilityTypes';
import { BigNum } from '@drift-labs/sdk';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';
import { cj } from 'src/utils/classNames';
import { copyToClipboard } from 'src/utils/browser';
import Button from 'src/components/primitives/Button/Button';
import Divider from 'src/components/primitives/Divider/Divider';
import { useWindowSizeIsGreaterThan } from 'src/hooks/useWindowSize';

interface Props extends WithClassName {
	totalRewards: BigNum;
	totalNumReferrals: BigNum;
	referrerEarnPercent: number | undefined;
	referralLink: string;
	isLoading: boolean;
}

export default function ReferralsPageStatsCard({
	className,
	totalRewards,
	totalNumReferrals,
	referrerEarnPercent,
	referralLink,
	isLoading,
}: Props) {
	return (
		<ValueWithLabelCard.Container className={cj(className, 'justify-between')}>
			<ValueWithLabelCard.Item
				label={
					<Tooltip
						content={`Referral rewards are automatically added to your unrealized P&L. You earn ${referrerEarnPercent}% of the trading fees from traders you've referred.`}
					>
						Total Rewards Earned
					</Tooltip>
				}
				className="sm:justify-start"
			>
				<CardItem
					displayType="currency"
					value={totalRewards}
					loading={isLoading}
					skeletonPlaceholderClassName="w-20"
					signDependentColor
				/>
			</ValueWithLabelCard.Item>

			<ValueWithLabelCard.Item
				label="Total Referrals"
				className="sm:justify-start"
			>
				<CardItem
					displayType="number"
					value={totalNumReferrals}
					loading={isLoading}
					skeletonPlaceholderClassName="w-10"
				/>
			</ValueWithLabelCard.Item>

			<Divider className="sm:hidden" />

			<ReferralLinkCardItem referralLink={referralLink} />
		</ValueWithLabelCard.Container>
	);
}

interface ReferralLinkCardItemProps {
	referralLink: string;
}

function ReferralLinkCardItem({ referralLink }: ReferralLinkCardItemProps) {
	const [copiedLink, setCopiedLink] = useState(false);

	const copyLink = () => {
		if (copiedLink) {
			return;
		}

		copyToClipboard(referralLink);

		setCopiedLink(true);

		setTimeout(() => {
			setCopiedLink(false);
		}, 3000);
	};

	// The copy button changes widths on desktop when clicked due to its text content
	// changing. In order to avoid horizontal layout shift of the other card items,
	// we need to add some buffer to the overall width of the container so that the
	// button has space on the right to expand into, rather than force-expanding left.
	const isWindowSizeGreaterThanSm = useWindowSizeIsGreaterThan('sm');
	const containerRef = useRef<HTMLDivElement>(null);
	const [containerWidth, setContainerWidth] = useState<number>();

	useLayoutEffect(() => {
		if (!containerRef.current) {
			return;
		}

		if (!isWindowSizeGreaterThanSm) {
			setContainerWidth(undefined);
			return;
		}

		// Currently `20px` is just the right amount of buffer needed.
		// This needs to be updated if we ever increase the size of the button.
		setContainerWidth(containerRef.current.clientWidth + 20);
	}, [isWindowSizeGreaterThanSm]);

	return (
		<ValueWithLabelCard.Item
			label="Referral Link"
			className="grid grid-cols-1 gap-x-md gap-y-sm"
		>
			<div
				// Note, we don't change layouts until `md` because `sm` is still _too_ small.
				className="flex gap-md not-md:flex-col md:items-center"
				style={{ width: containerWidth }}
				ref={containerRef}
			>
				<span className="max-w-full truncate sm:max-w-80 sm:typo-t3 lg:max-w-120">
					{referralLink}
				</span>

				<Button size="sm" className="not-sm:w-full" onClick={copyLink}>
					{copiedLink ? 'Copied!' : 'Copy'}
				</Button>
			</div>
		</ValueWithLabelCard.Item>
	);
}

interface CardItemProps {
	displayType: 'currency' | 'number';
	value: BigNum | undefined;
	loading: boolean;
	skeletonPlaceholderClassName?: string;
	signDependentColor?: boolean;
}

function CardItem({
	displayType,
	value,
	loading,
	skeletonPlaceholderClassName,
	signDependentColor,
}: CardItemProps) {
	const isCurrencyDisplay = displayType === 'currency';

	return (
		<NumberDisplay
			value={value ?? BigNum.zero()}
			type={displayType}
			decimals={isCurrencyDisplay ? 2 : undefined}
			className="text-text-default"
			loading={loading}
			skeletonClassName={cj('h-4 sm:h-6', skeletonPlaceholderClassName)}
			signDependentColor={signDependentColor}
		/>
	);
}
